// Měsíční kalendář pro výběr dne (mockup „Výběr termínu na měsíc“, schváleno 2. 10. 2026).
// Používá ho výběr termínu zkušebního tréninku (TerminPicker) a první trénink po návratu
// (NavratDialog). Listovat jde jen tento a příští měsíc, to hlídá i server (salesTerminy).
// Sloupce Po až Pá, víkend se ukáže jen tehdy, když v něm někdo má volno.
import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { addDays, dowOf, dm } from "./salesApi.js";

export const MESICE = ["", "Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const MESICE_GEN = ["", "ledna", "února", "března", "dubna", "května", "června", "července", "srpna", "září", "října", "listopadu", "prosince"];
const DNY_VELKE = ["", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota", "Neděle"];
const DNY_HLAVICKA = ["", "Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
const DNY_4 = ["", "pondělí", "úterý", "středu", "čtvrtek", "pátek", "sobotu", "neděli"];

// „Středa 7. října“ (velké = začátek věty).
export function denSlovy(iso, velke = true) {
  const [, m, d] = String(iso).split("-").map(Number);
  const den = velke ? DNY_VELKE[dowOf(iso)] : DNY_VELKE[dowOf(iso)].toLowerCase();
  return `${den} ${d}. ${MESICE_GEN[m]}`;
}
// „ve středu 7. října“, „v pondělí 5. října“ (do věty, kterou sales řekne klientce).
export function veDnSlovy(iso) {
  const [, m, d] = String(iso).split("-").map(Number);
  const dow = dowOf(iso);
  return `${dow === 3 || dow === 4 ? "ve" : "v"} ${DNY_4[dow]} ${d}. ${MESICE_GEN[m]}`;
}
export function volnychText(n) { return n === 1 ? "1 volný" : n >= 2 && n <= 4 ? `${n} volné` : `${n} volných`; }

/**
 * data: odpověď /api/sales?akce=terminy&mesic= (od, do, mesic, predchozi, dalsi, dny)
 * popisDne(den) → { dostupny, text, ton: "volno" | "plno" | "svatek" | null, kratce }
 */
export default function MesicniKalendar({ data, mesic, onMesic, vybrany, onVyber, popisDne, nacitam = false }) {
  const [y, m] = String(data?.mesic || mesic).split("-").map(Number);
  const nazev = `${MESICE[m]} ${y}`;
  const podleDne = new Map((data?.dny || []).map((d) => [d.datum, d]));
  const vikend = (data?.dny || []).some((d) => d.dow > 5);
  const sloupcu = vikend ? 7 : 5;
  const prvni = data?.od || `${mesic}-01`;
  const posledni = data?.do || addDays(`${m === 12 ? y + 1 : y}-${String(m === 12 ? 1 : m + 1).padStart(2, "0")}-01`, -1);
  const dny = [];
  for (let d = prvni; d <= posledni; d = addDays(d, 1)) if (dowOf(d) <= sloupcu) dny.push(d);
  const odsazeni = dny.length ? dowOf(dny[0]) - 1 : 0;
  const posun = (n) => { const x = new Date(Date.UTC(y, m - 1 + n, 1)); return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, "0")}`; };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button type="button" className="btn-mesic" onClick={() => onMesic(data?.predchozi || posun(-1))} disabled={!data?.predchozi || nacitam} aria-label="Předchozí měsíc"><ChevronLeft className="h-5 w-5" /></button>
        <div className="flex min-h-[44px] flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white px-2 text-center text-[16px] font-bold text-slate-900">{nazev}</div>
        <button type="button" className="btn-mesic" onClick={() => onMesic(data?.dalsi || posun(1))} disabled={!data?.dalsi || nacitam} aria-label="Další měsíc"><ChevronRight className="h-5 w-5" /></button>
      </div>
      <div className={`grid gap-1.5 ${sloupcu === 7 ? "grid-cols-7" : "grid-cols-5"}`}>
        {DNY_HLAVICKA.slice(1, sloupcu + 1).map((h) => (
          <div key={h} className="pb-0.5 text-center text-[12px] font-bold uppercase tracking-[0.06em] text-slate-500">{h}</div>
        ))}
        {Array.from({ length: odsazeni }).map((_, i) => <div key={`x${i}`} aria-hidden />)}
        {dny.map((datum) => {
          const d = podleDne.get(datum) || { datum, dow: dowOf(datum), pocet: 0, minulost: true };
          const p = d.minulost ? { dostupny: false } : popisDne(d);
          const zap = datum === vybrany;
          const den = Number(datum.slice(8, 10));
          const ton = zap ? "bg-white/20 text-white"
            : p.ton === "volno" ? "bg-brand-100 text-brand-deep"
              : p.ton === "svatek" ? "bg-amber-50 text-amber-800"
                : "bg-slate-100 text-slate-500";
          return (
            <button key={datum} type="button" onClick={() => onVyber(datum)} disabled={!p.dostupny || nacitam} aria-pressed={zap}
              aria-label={`${denSlovy(datum)}${p.text ? `, ${p.text}` : ""}`}
              className={`flex min-h-[56px] min-w-0 flex-col items-center justify-center gap-1 rounded-xl border px-0.5 py-1.5 transition sm:min-h-[64px] ${
                zap ? "border-brand bg-brand text-white"
                  : d.minulost ? "border-dashed border-slate-200 bg-slate-50 text-slate-400"
                    : p.dostupny ? "border-slate-300 bg-white text-slate-900 hover:border-brand/60"
                      : "border-slate-200 bg-slate-50 text-slate-400"}`}>
              <span className="text-[16px] font-bold tabular-nums leading-none"><span className="sm:hidden">{den}</span><span className="hidden sm:inline">{dm(datum)}</span></span>
              {p.text && (
                <span className={`max-w-full truncate rounded-full px-1.5 text-[12px] font-bold leading-[18px] sm:px-2 ${ton}`}>
                  <span className="sm:hidden">{p.kratce ?? p.text}</span><span className="hidden sm:inline">{p.text}</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
      {!data && <div className="text-center text-[13px] text-slate-500">Načítám volné termíny…</div>}
    </div>
  );
}
